/**
 * Shared direct destination matching for storefront search consumers.
 *
 * This module owns identity normalization and weighted direct ranking only.
 * Homepage discovery expansion and directory presentation remain consumer-owned.
 */
(() => {
  const normalize = (value) => String(value || '')
    .trim()
    .toLocaleLowerCase(document.documentElement.lang || undefined)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[._]/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const score = (destination, query) => {
    const name = normalize(destination.name);
    const defaultName = normalize(destination.defaultName);
    const iso2 = normalize(destination.iso2);
    const iso3 = normalize(destination.iso3);
    const aliases = (destination.aliases || []).map((alias) => normalize(alias));

    // Score bands intentionally separate exact country identifiers from prefix
    // and substring matches so short queries do not surface weak matches first.
    if (query === iso2) return 1000;
    if (query === iso3) return 990;
    if (query === name) return 980;
    if (query === defaultName) return 970;
    if (aliases.includes(query)) return 960;
    if (name.startsWith(query)) return 850;
    if (defaultName.startsWith(query)) return 840;
    if (aliases.some((alias) => alias.startsWith(query))) return 830;
    if ([name, defaultName, ...aliases].some((term) => term.includes(query))) return 700;
    return 0;
  };

  const directMatches = (destinations, rawQuery) => {
    const query = normalize(rawQuery);
    if (!query) return [];

    return destinations
      .map((destination) => ({ destination, score: score(destination, query) }))
      .filter((match) => match.score > 0)
      .sort((left, right) => (
        right.score - left.score
        || left.destination.name.localeCompare(right.destination.name)
      ));
  };

  window.DestinationSearchEngine = Object.freeze({
    normalize,
    score,
    directMatches,
  });
  window.dispatchEvent(new CustomEvent('destination-search-engine:ready'));
})();
